import React, { useState } from 'react';
import { Box, Typography, Snackbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

/**
 * Shared "how to use the bookmarklet" instructions.
 *
 * Why this exists:
 * Chrome refuses to run a `javascript:` URL that arrives by dragging a bookmark
 * onto a page. It is a browser-level anti-self-XSS protection, it applies on
 * every site regardless of the site's CSP, and page code cannot opt out of it.
 * Chrome silently lands on `about:blank#blocked` instead, which reads to the
 * user as "the preview is broken".
 *
 * The bookmarklet only works when it is CLICKED in the bookmarks bar. These
 * steps make that distinction explicit and call out the blocked interaction by
 * name, so the failure is recognisable rather than mysterious.
 */

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px dashed #86efac',
  },
  stepsHeading: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#1e3a5f',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  stepIcon: {
    fontSize: 15,
    color: '#3ecf8e',
    flexShrink: 0,
    marginTop: 1,
  },
  stepText: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: '#475569',
  },
  stepStrong: {
    color: '#1e3a5f',
    fontWeight: 600,
  },
  onceTag: {
    fontSize: '0.68rem',
    color: '#64748b',
    fontStyle: 'italic',
  },
  warning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    padding: theme.spacing(1, 1.25),
    background: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: 6,
  },
  warningIcon: {
    fontSize: 16,
    color: '#b45309',
    flexShrink: 0,
    marginTop: 1,
  },
  warningText: {
    fontSize: '0.72rem',
    lineHeight: 1.5,
    color: '#92400e',
  },
  fallback: {
    marginTop: theme.spacing(1.25),
  },
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: '0.72rem',
    color: '#1e3a5f',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
    '&:hover': {
      color: '#3ecf8e',
    },
    '&:disabled': {
      color: '#94a3b8',
      cursor: 'not-allowed',
      textDecoration: 'none',
    },
  },
  copyIcon: {
    fontSize: 14,
  },
  fallbackHint: {
    fontSize: '0.68rem',
    color: '#64748b',
    marginTop: theme.spacing(0.5),
    lineHeight: 1.5,
  },
}));

interface BookmarkletHowToProps {
  /** The full `javascript:` bookmarklet URL. Used by the copy fallback. */
  bookmarkletCode: string;
  /** False until the config has been pushed to cloud. Disables the copy fallback. */
  enabled: boolean;
}

const BookmarkletHowTo: React.FC<BookmarkletHowToProps> = ({ bookmarkletCode, enabled }) => {
  const classes = useStyles();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  /**
   * Synchronous copy via a throwaway textarea.
   *
   * This is tried first on purpose. `navigator.clipboard.writeText()` can stay
   * pending forever instead of rejecting when its permission/gesture check does
   * not pass, which would hang an `await` and leave the user with no feedback at
   * all. execCommand is deprecated but synchronous, so it either works or
   * returns false immediately.
   */
  const copyViaTextarea = (text: string): boolean => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      // Keep it off-screen and non-scrolling so selecting it cannot jump the page.
      textarea.style.position = 'fixed';
      textarea.style.top = '-1000px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopy = async () => {
    let copied = copyViaTextarea(bookmarkletCode);

    if (!copied && navigator.clipboard?.writeText) {
      try {
        // Bounded so a pending clipboard promise cannot hang the handler.
        await Promise.race([
          navigator.clipboard.writeText(bookmarkletCode),
          new Promise((_resolve, reject) => setTimeout(() => reject(new Error('clipboard timeout')), 1500)),
        ]);
        copied = true;
      } catch {
        copied = false;
      }
    }

    setToastMessage(
      copied
        ? 'Bookmarklet code copied. Create a new bookmark and paste this as its URL.'
        : 'Could not access the clipboard. Drag the green Quattr Preview button to your bookmarks bar instead.'
    );
    setToastOpen(true);
  };

  return (
    <Box className={classes.root}>
      <Typography className={classes.stepsHeading}>How to preview on your site</Typography>

      <Box className={classes.step}>
        <CloudUploadIcon className={classes.stepIcon} />
        <Typography className={classes.stepText}>
          Click <span className={classes.stepStrong}>Push to Cloud</span> to save this configuration.
        </Typography>
      </Box>

      <Box className={classes.step}>
        <BookmarkBorderIcon className={classes.stepIcon} />
        <Typography className={classes.stepText}>
          Drag the green <span className={classes.stepStrong}>Quattr Preview</span> button up onto your browser&apos;s
          bookmarks bar. <span className={classes.onceTag}>You only need to do this once.</span>
        </Typography>
      </Box>

      <Box className={classes.step}>
        <TouchAppIcon className={classes.stepIcon} />
        <Typography className={classes.stepText}>
          Open your website, then <span className={classes.stepStrong}>click the bookmark</span> in the bookmarks bar.
        </Typography>
      </Box>

      <Box className={classes.warning}>
        <ErrorOutlineIcon className={classes.warningIcon} />
        <Typography className={classes.warningText}>
          <strong>Re-drag the button after changing a template.</strong> The bookmark stores a snapshot of the preview
          code taken at the moment you drag it, so an older bookmark keeps running the version it was created with. If
          the preview looks out of date or does not appear, delete the bookmark and drag a fresh one.
        </Typography>
      </Box>

      <Box className={classes.fallback}>
        <button type="button" className={classes.copyButton} onClick={handleCopy} disabled={!enabled}>
          <FileCopyOutlinedIcon className={classes.copyIcon} />
          Copy bookmarklet code instead
        </button>
        <Typography className={classes.fallbackHint}>
          If dragging is awkward, copy the code, then add a new bookmark manually and paste it into the bookmark&apos;s
          URL field.
        </Typography>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default BookmarkletHowTo;
